import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { api, formatApiErrorDetail, API, inr } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Upload, X, Loader2, ImageIcon } from "lucide-react";

const EMOTIONS = [
  { v: "CONFIDENT", color: "#00C805" },
  { v: "FEARFUL", color: "#3A82F6" },
  { v: "FOMO", color: "#FF9900" },
  { v: "GREEDY", color: "#FFB800" },
  { v: "NEUTRAL", color: "#A1A1AA" },
];

export default function JournalDialog({ open, onClose, trade, onSaved }) {
  const [emotion, setEmotion] = useState(trade?.emotion || "");
  const [notes, setNotes] = useState(trade?.notes || "");
  const [screenshots, setScreenshots] = useState(trade?.screenshots || []);
  const [aiInsight, setAiInsight] = useState(trade?.ai_insight || "");
  const [busy, setBusy] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [previewUrls, setPreviewUrls] = useState({});

  useEffect(() => {
    if (open && trade) {
      setEmotion(trade.emotion || "");
      setNotes(trade.notes || "");
      setScreenshots(trade.screenshots || []);
      setAiInsight(trade.ai_insight || "");
    }
  }, [open, trade]);

  // Fetch blob URLs for screenshots (so we can render with auth).
  // Intentionally only re-runs when the SET of paths changes, not when previewUrls updates
  // (that would cause an infinite loop). We use a ref-like guard by joining paths.
  const screenshotPaths = screenshots.map((s) => s.path).join(",");
  useEffect(() => {
    let active = true;
    const newUrls = {};
    (async () => {
      let anyAdded = false;
      for (const s of screenshots) {
        if (previewUrls[s.path]) continue;
        try {
          const res = await api.get(`/files/${s.path}`, { responseType: "blob" });
          if (!active) return;
          newUrls[s.path] = URL.createObjectURL(res.data);
          anyAdded = true;
        } catch (err) {
          console.warn(`Failed to load screenshot ${s.name || s.path}:`, err.message);
        }
      }
      if (active && anyAdded) setPreviewUrls((p) => ({ ...p, ...newUrls }));
    })();
    return () => { active = false; };
  }, [screenshotPaths]);

  async function handleFiles(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const added = [];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push({ path: data.path, name: data.name, size: data.size, uploaded_at: new Date().toISOString() });
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
      }
    }
    setScreenshots((prev) => [...prev, ...added]);
    setUploading(false);
    e.target.value = "";
  }

  function removeShot(path) {
    setScreenshots((prev) => prev.filter((s) => s.path !== path));
  }

  async function save() {
    setBusy(true);
    try {
      await api.put(`/trades/${trade.id}`, { emotion: emotion || null, notes, screenshots });
      toast.success("Journal saved");
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function analyze() {
    setAnalyzing(true);
    try {
      // Auto-save current state first, then analyze
      await api.put(`/trades/${trade.id}`, { emotion: emotion || null, notes, screenshots });
      const { data } = await api.post(`/trades/${trade.id}/ai-analyze`);
      setAiInsight(data.insight);
      onSaved && onSaved();
      toast.success("AI insight generated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Analyze failed");
    } finally {
      setAnalyzing(false);
    }
  }

  if (!trade) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121318] border-[#27272A] text-white max-w-3xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="journal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="text-xs tracking-[0.25em] uppercase text-[#A1A1AA]">Trade Journal</span>
            <span className="text-base text-white font-mono">{trade.stock} · {trade.transaction}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Trade context strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#27272A] border border-[#27272A] text-xs">
          <Stat label="Entry" value={trade.rate?.toFixed(2)} />
          <Stat label="Qty" value={trade.lots ? `${trade.quantity} (${trade.lots} × ${trade.lot_size})` : trade.quantity} />
          <Stat label="Invested" value={inr(trade.total_invested)} />
          <Stat label="Status" value={trade.status} />
        </div>

        {/* Emotion */}
        <div>
          <Label className="text-[10px] tracking-[0.25em] uppercase text-[#71717A]">Emotion at entry</Label>
          <div className="flex flex-wrap gap-2 mt-2" data-testid="emotion-row">
            {EMOTIONS.map((e) => {
              const active = emotion === e.v;
              return (
                <button
                  key={e.v}
                  type="button"
                  data-testid={`emotion-${e.v}`}
                  onClick={() => setEmotion(active ? "" : e.v)}
                  className={`px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold rounded-sm border transition-colors ${
                    active
                      ? "bg-white/5 text-white"
                      : "border-[#27272A] text-[#71717A] hover:text-white hover:border-[#3F3F46]"
                  }`}
                  style={active ? { borderColor: e.color, color: e.color } : {}}
                >
                  {e.v}
                </button>
              );
            })}
          </div>
        </div>

        {/* Notes */}
        <div>
          <Label className="text-[10px] tracking-[0.25em] uppercase text-[#71717A]">Notes — setup, context, lessons</Label>
          <Textarea
            data-testid="journal-notes"
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Why did you take this trade? What was the setup? What did you learn?"
            className="mt-2 bg-[#08080A] border-[#27272A] text-white rounded-sm placeholder:text-[#71717A]"
          />
        </div>

        {/* Screenshots */}
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-[10px] tracking-[0.25em] uppercase text-[#71717A]">Chart Screenshots</Label>
            <Button
              type="button"
              data-testid="journal-upload-btn"
              variant="outline"
              size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="bg-transparent border-[#27272A] text-[#A1A1AA] hover:bg-[#1A1B23] hover:text-white rounded-sm h-7"
            >
              {uploading ? <Loader2 className="h-3 w-3 animate-spin mr-2" /> : <Upload className="h-3 w-3 mr-2" />}
              <span className="text-[10px] tracking-wider">Add Image</span>
            </Button>
          </div>
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleFiles} className="hidden" data-testid="journal-file-input" />
          {screenshots.length === 0 ? (
            <div className="mt-2 border border-dashed border-[#27272A] p-6 text-center text-[#71717A] text-xs rounded-sm">
              <ImageIcon className="h-5 w-5 mx-auto mb-2 opacity-50" />
              No screenshots yet
            </div>
          ) : (
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="screenshots-grid">
              {screenshots.map((s) => (
                <div key={s.path} className="relative group border border-[#27272A] rounded-sm overflow-hidden bg-[#08080A]" data-testid={`screenshot-${s.path}`}>
                  {previewUrls[s.path] ? (
                    <img src={previewUrls[s.path]} alt={s.name} className="w-full h-32 object-cover" />
                  ) : (
                    <div className="w-full h-32 flex items-center justify-center text-[#71717A]">
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => removeShot(s.path)}
                    className="absolute top-1 right-1 h-6 w-6 bg-black/70 text-white rounded-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#FF3B30]"
                    data-testid={`remove-screenshot-${s.path}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-2 py-1 text-[10px] text-white truncate font-mono">
                    {s.name}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* AI Insight */}
        <div className="border border-[#27272A] bg-[#08080A] rounded-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5 text-[#FF9900]" />
              <span className="text-[10px] tracking-[0.25em] uppercase text-[#FF9900] font-semibold">AI Coach Insight</span>
            </div>
            <Button
              data-testid="ai-analyze-btn"
              size="sm"
              onClick={analyze}
              disabled={analyzing}
              className="bg-[#FF9900] hover:bg-[#FF9900]/90 text-black rounded-sm h-7 text-[10px] tracking-wider font-semibold"
            >
              {analyzing ? <><Loader2 className="h-3 w-3 animate-spin mr-2" /> Analyzing</> : <>{aiInsight ? "Re-Analyze" : "Analyze with AI"}</>}
            </Button>
          </div>
          {aiInsight ? (
            <div data-testid="ai-insight-text" className="text-sm text-[#F8F9FA] whitespace-pre-wrap leading-relaxed">{aiInsight}</div>
          ) : (
            <div className="text-xs text-[#71717A]">Save your notes/emotion and click &ldquo;Analyze&rdquo; to get an AI coach review of this trade.</div>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="journal-close"
            className="bg-transparent border-[#27272A] text-[#A1A1AA] hover:bg-[#1A1B23] hover:text-white rounded-sm">Close</Button>
          <Button onClick={save} disabled={busy} data-testid="journal-save"
            className="bg-[#007AFF] hover:bg-[#007AFF]/90 text-white rounded-sm">
            {busy ? "Saving…" : "Save Journal"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-[#121318] px-3 py-2">
      <div className="text-[9px] tracking-[0.2em] uppercase text-[#71717A]">{label}</div>
      <div className="text-sm font-mono text-white mt-0.5 truncate">{value ?? "—"}</div>
    </div>
  );
}
